/**
 * buildPeriods — shared period-generation logic for the availability calendar.
 *
 * Used by both the inventory full-availability view (current page's units)
 * and the single-unit detail card.
 *
 * Key behaviors:
 * - Generates periods for a 12-month rolling horizon from a given start date
 * - Supports month, half-month, week, and day granularities
 * - Returns InventoryAvailabilityPeriod[] compatible with AvailabilityGrid
 */
import type { InventoryAvailabilityPeriod } from '../components/inventory-availability-shared';
import type { Granularity } from '../components/availability-granularity-select';

// ─── PL month names (data formatters, not JSX strings — i18n-exempt per gotcha #6) ─

const MONTH_LABELS_FULL = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

export const MONTH_LABELS_SHORT = [
  'sty',
  'lut',
  'mar',
  'kwi',
  'maj',
  'cze',
  'lip',
  'sie',
  'wrz',
  'paź',
  'lis',
  'gru',
];

function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

function toIso(year: number, month1: number, day: number): string {
  return `${year}-${String(month1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Generates availability periods for the given granularity.
 *
 * @param granularity - Column granularity: 'month' | 'half-month' | 'week' | 'day'
 * @param horizonMonths - Number of months to generate (default: 12)
 * @param startDate - Start date for period generation (default: today, UTC)
 */
// eslint-disable-next-line max-lines-per-function, complexity -- 4-branch granularity dispatch; each branch is simple but they add up
export function buildPeriods(
  granularity: Granularity,
  horizonMonths = 12,
  startDate?: Date,
): InventoryAvailabilityPeriod[] {
  const now = startDate ?? new Date();
  const startYear = now.getUTCFullYear();
  const startMonth = now.getUTCMonth(); // 0-indexed
  const endDate = new Date(Date.UTC(startYear, startMonth + horizonMonths, 0));
  const periods: InventoryAvailabilityPeriod[] = [];

  if (granularity === 'month') {
    for (let i = 0; i < horizonMonths; i++) {
      const totalMonth = startMonth + i;
      const year = startYear + Math.floor(totalMonth / 12);
      const month = totalMonth % 12;
      const month1 = month + 1;
      const last = daysInMonth(year, month1);
      const key = `${year}-${String(month1).padStart(2, '0')}`;
      periods.push({
        id: key,
        label: `${MONTH_LABELS_FULL[month] ?? ''} ${year}`,
        monthLabel: String(year),
        start: toIso(year, month1, 1),
        end: toIso(year, month1, last),
      });
    }
  } else if (granularity === 'half-month') {
    for (let i = 0; i < horizonMonths; i++) {
      const totalMonth = startMonth + i;
      const year = startYear + Math.floor(totalMonth / 12);
      const month = totalMonth % 12;
      const month1 = month + 1;
      const last = daysInMonth(year, month1);
      const key = `${year}-${String(month1).padStart(2, '0')}`;
      const shortLabel = MONTH_LABELS_SHORT[month] ?? '';
      const fullMonthLabel = `${MONTH_LABELS_FULL[month] ?? ''} ${year}`;
      periods.push(
        {
          id: `${key}-h1`,
          label: `1–15 ${shortLabel}`,
          monthLabel: fullMonthLabel,
          start: toIso(year, month1, 1),
          end: toIso(year, month1, 15),
        },
        {
          id: `${key}-h2`,
          label: `16–${last} ${shortLabel}`,
          monthLabel: fullMonthLabel,
          start: toIso(year, month1, 16),
          end: toIso(year, month1, last),
        },
      );
    }
  } else if (granularity === 'week') {
    const current = new Date(Date.UTC(startYear, startMonth, 1));
    while (current <= endDate) {
      const weekEnd = new Date(current);
      weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
      const clampedEnd = weekEnd > endDate ? endDate : weekEnd;
      const startIso = current.toISOString().slice(0, 10);
      const endIso = clampedEnd.toISOString().slice(0, 10);
      const month = current.getUTCMonth();
      periods.push({
        id: `w-${startIso}`,
        label: `${current.getUTCDate()} ${MONTH_LABELS_SHORT[month] ?? ''}`,
        monthLabel: `${MONTH_LABELS_FULL[month] ?? ''} ${current.getUTCFullYear()}`,
        start: startIso,
        end: endIso,
      });
      current.setUTCDate(current.getUTCDate() + 7);
    }
  } else {
    // day
    const current = new Date(Date.UTC(startYear, startMonth, 1));
    while (current <= endDate) {
      const iso = current.toISOString().slice(0, 10);
      const month = current.getUTCMonth();
      periods.push({
        id: `d-${iso}`,
        label: String(current.getUTCDate()),
        monthLabel: `${MONTH_LABELS_FULL[month] ?? ''} ${current.getUTCFullYear()}`,
        start: iso,
        end: iso,
      });
      current.setUTCDate(current.getUTCDate() + 1);
    }
  }

  return periods;
}
